const RESULTS_REGEX = /<results>\s*([\s\S]*?)\s*<\/results>/i;
export class AgentResultsParseError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AgentResultsParseError';
    }
}
/**
 * Concatenates assistant text from Agent Studio `events`, extracts the JSON inside `<results>...</results>`, and parses it.
 */
export function extractResultsJsonText(events) {
    const chunks = [];
    for (const event of events) {
        const parts = event?.content?.parts ?? [];
        for (const part of parts) {
            if (part.text) {
                chunks.push(part.text);
            }
        }
    }
    const combined = chunks.join('\n');
    return extractResultsJsonTextFromPlainText(combined);
}
/** Extract inner JSON string from `<results>...</results>` in arbitrary assistant text (e.g. streamed tokens). */
export function extractResultsJsonTextFromPlainText(combined) {
    const match = combined.match(RESULTS_REGEX);
    return match?.[1]?.trim();
}
export function parseAgentSignatureResults(text) {
    let parsed;
    try {
        parsed = JSON.parse(text);
    }
    catch {
        throw new AgentResultsParseError('Invalid JSON inside <results> tags');
    }
    if (!parsed || typeof parsed !== 'object' || !('fields' in parsed)) {
        throw new AgentResultsParseError('Parsed JSON must be an object with a "fields" array');
    }
    const fieldsRaw = parsed.fields;
    if (!Array.isArray(fieldsRaw)) {
        throw new AgentResultsParseError('"fields" must be an array');
    }
    const fields = fieldsRaw.map((row, index) => {
        if (!row || typeof row !== 'object') {
            throw new AgentResultsParseError(`fields[${index}] must be an object`);
        }
        const r = row;
        const label = typeof r.label === 'string' ? r.label : '';
        const page = typeof r.page === 'number' && Number.isFinite(r.page) ? Math.floor(r.page) : NaN;
        const x = toFiniteNumber(r.x);
        const y = toFiniteNumber(r.y);
        const width = toFiniteNumber(r.width);
        const height = toFiniteNumber(r.height);
        if (page < 1 || !Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
            throw new AgentResultsParseError(`fields[${index}] has invalid numeric geometry or page`);
        }
        if (width <= 0 || height <= 0) {
            throw new AgentResultsParseError(`fields[${index}] width and height must be positive`);
        }
        return { label, page, x, y, width, height };
    });
    return { fields };
}
function toFiniteNumber(v) {
    return typeof v === 'number' && Number.isFinite(v) ? v : NaN;
}
/**
 * Full pipeline: events → optional `<results>` JSON → typed result.
 */
export function parseSignatureFieldsFromEvents(events) {
    const jsonText = extractResultsJsonText(events);
    if (jsonText === undefined) {
        throw new AgentResultsParseError('No <results>...</results> block found in agent response');
    }
    return parseAgentSignatureResults(jsonText);
}
export function parseSignatureFieldsFromAssistantText(combined) {
    const jsonText = extractResultsJsonTextFromPlainText(combined);
    if (jsonText === undefined) {
        throw new AgentResultsParseError('No <results>...</results> block found in agent response');
    }
    return parseAgentSignatureResults(jsonText);
}
